/* eslint-disable react/prop-types */
import React from 'react';
import PropTypes from 'prop-types';
import { graphql } from 'gatsby';
import { ThemeProvider } from 'styled-components';
import { withPreview } from 'gatsby-source-prismic';

import Layout from '../components/Layout';
import theme from '../ui/theme';
import Grid from '../components/Grid';
import Cell from '../components/Cell';
import Block from '../components/Block';
import H2 from '../components/H2';
import P from '../components/P';
import NewsList from '../components/NewsList';
import RichTextHtml from '../components/RichTextHtml';
import SEO from '../components/SEO';
import Image from '../components/Image';
import MediaDisplay from '../components/MediaDisplay';
import ContainerHeader from '../components/ContainerHeader';
import CTAAnchor from '../components/CTAAnchor';
import { hasCallToAction } from '../utils';

const NewsTemplate = ({ data: { prismicNews }, location, pageContext }) => (
  <ThemeProvider theme={theme}>
    <Layout
      selectedLocale={pageContext.lang}
      location={location}
      hasHero={false}
    >
      <SEO
        selectedLocale={pageContext.lang}
        location={location}
        specifics={{
          title: prismicNews.data.meta_title || prismicNews.data.title.text,
          description: prismicNews.data.meta_description,
          image: prismicNews.data.share_image.url,
        }}
      />

      <ContainerHeader
        py={{ mobile: 'baseUp6', smallTablet: 'baseUp8', tablet: 'baseUp10' }}
        backgroundColor="grayLight"
      />

      <Grid>
        <Cell size={{ mobile: 1, smallTablet: 1 / 2 }}>
          <Block
            px="baseUnit"
            py={{
              mobile: 'baseUnit',
              smallTablet: 'baseUp12',
              tablet: 'baseUp20',
            }}
            pt={{ mobile: 'baseUp8' }}
          >
            <Image content={prismicNews.data.image} />
            {prismicNews.data.video && (
              <Block mt="baseUnit">
                <MediaDisplay content={{ video: prismicNews.data.video }} />
              </Block>
            )}
          </Block>
        </Cell>
        <Cell size={{ mobile: 1, smallTablet: 1 / 2 }}>
          <Block
            px="baseUnit"
            py={{
              mobile: 'baseUnit',
              smallTablet: 'baseUp12',
              tablet: 'baseUp20',
            }}
            pb={{ mobile: 'baseUp8' }}
          >
            <H2 color="coral">{prismicNews.data.title.text}</H2>
            <P mb={{ mobile: 'baseUp4', smallTablet: 'baseUp8' }}>
              {prismicNews.data.date}
            </P>

            <RichTextHtml html={prismicNews.data.text.html} />

            {hasCallToAction(prismicNews.data) && (
              <Block pt="baseUp4">
                <CTAAnchor
                  text={prismicNews.data.call_to_action_text.text}
                  link={prismicNews.data.call_to_action_link}
                  colorTheme={prismicNews.data.call_to_action_color}
                />
              </Block>
            )}
          </Block>
        </Cell>
      </Grid>

      <Block
        pb={{
          mobile: 'baseUnit',
          smallTablet: 'baseUp12',
          tablet: 'baseUp20',
        }}
      >
        <NewsList
          selectedLocale={pageContext.lang}
          excludeUid={prismicNews.uid}
          numNewsItems={10}
        />
      </Block>
    </Layout>
  </ThemeProvider>
);

NewsTemplate.propTypes = {
  data: PropTypes.shape({
    prismicNews: PropTypes.shape({
      uid: PropTypes.string,
      data: PropTypes.shape({
        meta_title: PropTypes.string,
        meta_description: PropTypes.string,
        share_image: PropTypes.shape({
          url: PropTypes.string,
        }),
        date: PropTypes.string,
        title: PropTypes.shape({
          text: PropTypes.string,
        }),
        text: PropTypes.shape({
          html: PropTypes.string,
        }),
        image: PropTypes.shape({
          url: PropTypes.string,
          alt: PropTypes.string,
        }),
        video: PropTypes.shape({
          title: PropTypes.string,
          embed_url: PropTypes.string,
          provider_name: PropTypes.string,
        }),
      }),
    }),
  }).isRequired,
  location: PropTypes.shape({}).isRequired,
  pageContext: PropTypes.shape({ lang: PropTypes.string }).isRequired,
};

export default withPreview(NewsTemplate);

export const query = graphql`
  query ($id: String!) {
    prismicNews(id: { eq: $id }) {
      uid
      data {
        meta_title
        meta_description
        share_image {
          url
        }
        date(formatString: "MMMM DD, YYYY")
        title {
          text
        }
        text {
          html
        }
        image {
          alt
          localFile {
            childImageSharp {
              fluid(maxWidth: 600) {
                ...GatsbyImageSharpFluid
              }
            }
          }
        }
        video {
          title
          embed_url
          provider_name
        }
        call_to_action_color
        call_to_action_text {
          text
        }
        call_to_action_link {
          link_type
          url
          lang
          target
        }
      }
    }
  }
`;
